import { ApolloProvider } from '@apollo/client';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { Box, GlobalStyles as GDSGlobalStyles, ToastProvider } from '@bechtel/gds';
import { AppUserProvider, useAppPermissions, useAppUser } from '@bechtel/ht-clients-common';
import { AuthPermission, User } from '@bechtel/ht-types';
import { map } from 'lodash';
import { FC, Suspense, lazy, useEffect, useState } from 'react';
import { Outlet, RouterProvider, createBrowserRouter, redirect, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { client } from '~/apollo-client';
import { appTheme } from '~/app-theme';
import { ApplicationGlobalStyles, AuthRoute, ErrorBoundary, ErrorComponent } from '~/components';
import { Navigation } from '~/components/navigation';
import { doesCookieExists, saveCookie } from '~/utilities';
import { Content } from './components/content';
import { InitialScreen } from './components/initial-screen';
import { NewReservation } from './components/new-reservation';
import ReturnToolOwned from './components/tool-owned';
import { useGetRolePermissionList, useGetUomList } from './hooks';
import './utilities/init-fontawesome';
import ProjectAdmin from './views/project-admin';
import { CreateReservation } from './views/reservations/create';
import { MyCart } from './views/reservations/create/my-cart/index';
import { ToolOrderConfirmation } from './views/reservations/create/my-cart/order-confirmation';

const Reservations = lazy(() => import('~/views/reservations'));
const Dispatch = lazy(() => import('~/views/dispatch'));
const MapView = lazy(() => import('~/views/map'));
const Dashboard = lazy(() => import('~/views/dashboard'));
const Returns = lazy(() => import('~/views/returns'));
const Transfer = lazy(() => import('~/views/transfer'));
const MyTools = lazy(() => import('~/views/my-tools'));
const Inventory = lazy(() => import('~/views/inventory'));

interface AppProps {
  instance: PublicClientApplication;
}

const AppLayout = () => {
  //pre caching uoms list at app startup
  const { getUomList } = useGetUomList();
  const { getRolePermissionList } = useGetRolePermissionList();
  const { appUser } = useAppUser();
  const navigate = useNavigate();

  const checkUserType = () => {
    if (appUser && appUser.permissionScopes && appUser.permissionScopes.length > 0) {
      const userPermissions = map(appUser.permissionScopes, 'permission');
      const isToolRoomUser = userPermissions.some(
        (item) =>
          item?.name &&
          [
            AuthPermission.PICK_INVENTORY,
            AuthPermission.EDIT_TOOL_INVENTORY,
            AuthPermission.CREATE_TOOL_TRANSFER,
            AuthPermission.VIEW_USER_ADMIN,
          ].includes(item?.name as AuthPermission)
      );
      isToolRoomUser ? navigate('/dispatch') : navigate('/reservations');
    }
  };

  useEffect(() => {
    getUomList();
    getRolePermissionList();
  }, []);

  useEffect(() => {
    checkUserType();
  }, [appUser]);

  return (
    <Box flexDirection={{ sm: 'row' }} style={{ width: '100%', height: '100vh' }}>
      <Box>
        <Navigation />
      </Box>
      {/* <Box flexDirection={{ sm: 'column' }} style={{ width: '100%' }}> */}
      <Content>
        <Box style={{ width: '100%' }}>
          <div style={{ flex: '1', width: '100%', height: '100%' }}>
            <Outlet />
          </div>
        </Box>
      </Content>
      {/* </Box> */}
    </Box>
  );
};

const rootRouter = (hasPermissions: any, appUser: User) => {
  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <>
          <AppLayout />
        </>
      ),
      errorElement: <ErrorComponent error={'My error'} />,
      children: [
        {
          path: 'reservations/*',
          element: <Reservations />,
          children: [
            {
              path: 'new',
              element: <NewReservation />,
            },
            {
              path: 'new/:toolRoomId',
              element: <CreateReservation />,
            },
            {
              path: 'draft/:id',
              element: <CreateReservation />,
            },
            {
              path: 'confirm',
              element: <ToolOrderConfirmation></ToolOrderConfirmation>,
            },
            {
              path: 'toolorder/:id',
              element: <MyCart></MyCart>,
            },
          ],
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.RESERVATION_MODULE)) throw redirect('/');
            return true;
          },
        },
        {
          path: 'project-admin/*',
          element: <ProjectAdmin />,

          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.VIEW_USER_ADMIN)) throw redirect('/');
            return true;
          },
        },
        {
          path: 'map',
          element: <MapView />,
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.MAP_MODULE)) throw redirect('/');
            return true;
          },
        },

        {
          path: 'mytools',
          element: <MyTools />,
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.FAVORITES_MODULE)) throw redirect('/');
            return true;
          },
        },

        {
          path: 'dispatch/*',
          element: <Dispatch />,
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.DISPATCH_MODULE)) throw redirect('/');
            return true;
          },
        },
        {
          path: 'transfer/*',
          element: <Transfer />,
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.TOOL_TRANSFER_MODULE)) throw redirect('/');
            return true;
          },
        },
        {
          path: 'returns/*',
          element: <Returns />,
          children: [
            {
              path: 'new/',
              element: <ReturnToolOwned />,
            },
          ],
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.RETURN_MODULE)) throw redirect('/');
            return true;
          },
        },
        {
          path: 'inventory/*',
          element: <Inventory />,
          children: [],
          loader: () => {
            if (appUser && !hasPermissions(AuthPermission.INVENTORY_MODULE)) throw redirect('/');
            return true;
          },
        },
      ],
    },
  ]);
  return router;
};

const AuthenticatedApp = () => {
  const { hasPermission } = useAppPermissions();
  const { appUser } = useAppUser();
  const [cookieExist, setCookieExist] = useState(doesCookieExists());
  useEffect(() => {
    async function SetCookie() {
      try {
        await saveCookie();
      } catch (error) {
        setCookieExist(false);
      }
    }
    if (!cookieExist) SetCookie();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={appTheme}>
        <ToastProvider>
          <GDSGlobalStyles />
          <ApplicationGlobalStyles />
          {/* TODO: Add AuthorizedRoute component after app user query is done */}
          {/* <div data-testid="app-container"> */}
          <AuthRoute>
            <RouterProvider router={rootRouter(hasPermission, appUser)} />
          </AuthRoute>
          {/* </div> */}

          {/* This fixes the UI issue in web app for now. TODO: fix toasts <ToastContent position={ToastPosition.MIDDLE} /> */}
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const App: FC<AppProps> = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        errorComponent={ErrorComponent}
        loadingComponent={InitialScreen}
      >
        <ErrorBoundary>
          <Suspense fallback={<div>Loading</div>}>
            <ApolloProvider client={client}>
              <AppUserProvider>
                <AuthenticatedApp />
              </AppUserProvider>
            </ApolloProvider>
          </Suspense>
        </ErrorBoundary>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

export default App;
// trigger deployment 27
// sample change for my feature
